import {Box, Button, IconButton, Popover, Stack, Tooltip, Typography} from "@mui/material";
import {grey, lightBlue} from "@mui/material/colors";
import {DateCalendar, LocalizationProvider, PickersDay} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {IconCalendar, IconX} from "@tabler/icons-react";
import moment from "moment";
import {useState} from "react";

const RangeDay = ({day, rangeStart, rangeEnd, onDayClick, onDayHover, outsideCurrentMonth, ...pickersDayProps}) => {
  const isStart = rangeStart && !outsideCurrentMonth && day.isSame(rangeStart, "day");
  const isEnd = rangeEnd && !outsideCurrentMonth && day.isSame(rangeEnd, "day");
  const inRange = rangeStart && rangeEnd && !outsideCurrentMonth &&
    !rangeStart.isSame(rangeEnd, "day") &&
    day.isAfter(rangeStart, "day") &&
    day.isBefore(rangeEnd, "day");
  const showBg = (isStart || inRange || isEnd) && rangeStart && rangeEnd && !rangeStart.isSame(rangeEnd, "day");

  return (
    <Box
      onMouseEnter={() => !outsideCurrentMonth && onDayHover(day)}
      sx={{position: "relative", display: "flex", alignItems: "center", justifyContent: "center"}}
    >
      {showBg && (
        <Box sx={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          height: 36,
          left: isStart ? "50%" : 0,
          right: isEnd ? "50%" : 0,
          bgcolor: lightBlue[50],
          zIndex: 0,
          pointerEvents: "none",
        }}/>
      )}
      <PickersDay
        {...pickersDayProps}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        onDaySelect={() => {}}
        onClick={() => !outsideCurrentMonth && onDayClick(day)}
        selected={isStart || isEnd}
        sx={{
          position: "relative",
          zIndex: 1,
          ...((isStart || isEnd) ? {
            bgcolor: `${lightBlue[500]} !important`,
            color: "white !important",
            "&:hover": {bgcolor: `${lightBlue[600]} !important`},
          } : {}),
        }}
      />
    </Box>
  );
};

/**
 * Single-calendar range date picker.
 * First click sets the start date, second click sets the end date.
 *
 * @param {Date|null}  startDate
 * @param {Date|null}  endDate
 * @param {function}   onStartChange  – called with a JS Date
 * @param {function}   onEndChange    – called with a JS Date
 */
const DateRangePicker = ({startDate, endDate, onStartChange, onEndChange, triggerSx}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
    setSelecting(false);
    setAnchor(null);
    setHoverDate(null);
  };

  const handleDayClick = (day) => {
    if (!selecting) {
      setAnchor(day.clone());
      setSelecting(true);
      onStartChange(day.clone().startOf("day").toDate());
      onEndChange(null);
    } else {
      const [start, end] = anchor.isBefore(day) ? [anchor, day] : [day, anchor];
      onStartChange(start.clone().startOf("day").toDate());
      onEndChange(end.clone().endOf("day").toDate());
      setSelecting(false);
      setAnchor(null);
      setHoverDate(null);
      setAnchorEl(null);
    }
  };

  const handleDayHover = (day) => {
    if (selecting) setHoverDate(day.clone());
  };

  const handleClear = () => {
    onStartChange?.(null);
    onEndChange?.(null);
    handleClose();
  };

  // Compute the effective visual range for highlighting
  let rangeStart = null;
  let rangeEnd = null;
  if (selecting && anchor && hoverDate) {
    [rangeStart, rangeEnd] = anchor.isBefore(hoverDate) ? [anchor, hoverDate] : [hoverDate, anchor];
  } else if (selecting && anchor) {
    rangeStart = anchor;
  } else {
    rangeStart = startDate ? moment(startDate) : null;
    rangeEnd = endDate ? moment(endDate) : null;
  }

  const isToday = startDate && endDate &&
    moment(startDate).isSame(moment().startOf("day"), "minute") &&
    moment(endDate).isSame(moment().endOf("day"), "minute");

  const label = startDate && endDate && !isToday
    ? `${moment(startDate).format("DD MMM YYYY")} — ${moment(endDate).format("DD MMM YYYY")}`
    : "Select date range";

  return (
    <>
      <Button
        size={"small"}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={triggerSx || {
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          textTransform: "none",
          color: grey[700],
          bgcolor: grey[100],
          "&:hover": {bgcolor: grey[200]},
        }}
      >
        <IconCalendar size={16} color={grey[600]}/>
        <Typography variant="body2" noWrap sx={{fontSize: "inherit"}}>
          {label}
        </Typography>
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        transitionDuration={0}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        slotProps={{paper: {sx: {borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", mt: 1}}}}
      >
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{pt: 1.5, px: 2, pb: 0}}>
            <Typography variant="caption" fontWeight={600} color={selecting ? lightBlue[600] : grey[500]}>
              {selecting ? "Now select an end date" : "Select a start date"}
            </Typography>
            {(startDate || endDate) && (
              <Tooltip title="Clear">
                <IconButton size="small" onClick={handleClear} sx={{p: 0.25}}>
                  <IconX size={14}/>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <DateCalendar
            value={null}
            onChange={() => {}}
            reduceAnimations
            maxDate={moment()}
            referenceDate={startDate ? moment(startDate) : moment()}
            slots={{day: RangeDay}}
            slotProps={{
              day: () => ({rangeStart, rangeEnd, onDayClick: handleDayClick, onDayHover: handleDayHover}),
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default DateRangePicker;
